<template lang="pug">
    div(
        :class="styles.root"
    )
        div(
            :class="styles.wrapper"
        )
            div(
                v-for="(item, index) in list"
                :class="styles.item"
                :key="`${item.id}-${index}`"
                ref="itemRefs"
                v-on:click="handleClick(index)"
            )
                TheText(
                    tag="span"
                    name="titleM"
                    color="foreground"
                ) {{ item.text }}
            div(
                :class="styles.box"
                :style="boxStyle"
            )
</template>

<script lang="ts">
import Vue, {
    nextTick,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
    toRefs,
    useCssModule,
    watch,
} from 'vue'

import { TheText } from '@/shared/ui/TheText'

import { TabsEmits, TabsProps } from './index'

export default Vue.extend({
    name: 'Tabs',
    components: { TheText },
    props: {
        value: {
            type: [String, Number] as PropType<TabsProps['value']>,
            required: true,
        },
        list: {
            type: Array as PropType<TabsProps['list']>,
            required: true,
        },
    },
    setup(props, { emit }: { emit: TabsEmits }) {
        const styles = useCssModule()
        const itemRefs = ref<HTMLElement[]>([])
        const { list, value } = toRefs(props)

        const boxStyle = ref<{
            transform: string
            width: string
            height: string
            transition?: string
        }>({
            transform: 'translateX(0)',
            width: '0',
            height: '0',
        })
        const isMounted = ref(false)

        function updateBoxPosition(index: number) {
            const el = itemRefs.value[index]
            if (el) {
                const { offsetLeft, offsetWidth, offsetHeight } = el

                if (!isMounted.value) {
                    // Первое выставление без анимации
                    boxStyle.value = {
                        transform: `translateX(${offsetLeft}px)`,
                        width: `${offsetWidth}px`,
                        height: `${offsetHeight}px`,
                    }
                } else {
                    // После маунта - плавная анимация
                    boxStyle.value = {
                        transform: `translateX(${offsetLeft}px)`,
                        width: `${offsetWidth}px`,
                        height: `${offsetHeight}px`,
                        transition: 'transform 0.3s ease, width 0.3s ease', // добавляем анимацию
                    }
                }
            }
        }

        function findIndexByValue(value: Maybe<number | string>) {
            return list.value.findIndex(item => item.id === value)
        }

        function handleClick(index: number) {
            emit('input', list.value[index].id)
        }

        function onResize() {
            const index = findIndexByValue(value.value)
            if (index !== -1) {
                nextTick(() => updateBoxPosition(index))
            }
        }

        onMounted(() => {
            nextTick(() => {
                const initialIndex = findIndexByValue(value.value)
                if (initialIndex !== -1) updateBoxPosition(initialIndex)
                isMounted.value = true // После первого маунта разрешаем анимацию
            })

            window.addEventListener('resize', onResize, { passive: true })
        })

        onBeforeUnmount(() => {
            window.removeEventListener('resize', onResize)
        })

        watch(value, newVal => {
            const index = findIndexByValue(newVal)
            if (index !== -1) {
                nextTick(() => updateBoxPosition(index))
            }
        })

        return {
            styles,
            boxStyle,
            handleClick,
            itemRefs,
        }
    },
})
</script>

<style module lang="scss" src="./Tabs.module.scss" />
